export function optimizeMarkdownImageURL(src: string): string {
  try {
    const withPrefix =
      !src.startsWith("http:") && !src.startsWith("https:")
        ? "https:" + src
        : src;
    const url = new URL(withPrefix);
    url.searchParams.set("fm", "webp");
    url.searchParams.set("q", "75");
    url.searchParams.set("w", "672");
    return url.href;
  } catch (ex) {
    console.error(ex);
    return src;
  }
}

export function optimizeImageUrlLetterBox({
  url = "",
  width = 0,
  height = 0,
}: {
  url: string;
  width: number;
  height: number;
}): { newHref: string; newWidth: number; newHeight: number } {
  if (!url) {
    return { newHref: "", newWidth: 0, newHeight: 0 };
  }

  const maxWidth = 672;
  const maxHeight = 378;
  const scale = maxHeight / height;
  let newHeight = Math.floor(height * scale);
  let newWidth = Math.floor(width * scale);
  if (newWidth > maxWidth) {
    const horizontalScale = newWidth / maxWidth;
    newHeight = Math.floor(newHeight * horizontalScale);
    newWidth = Math.floor(newWidth * horizontalScale);
  }

  try {
    const withPrefix =
      !url.startsWith("http:") && !url.startsWith("https:")
        ? "https:" + url
        : url;
    const newUrl = new URL(withPrefix);
    newUrl.searchParams.set("fm", "webp");
    newUrl.searchParams.set("w", newWidth.toString());
    newUrl.searchParams.set("h", newHeight.toString());
    const newHref = newUrl.href;
    return { newHref, newWidth, newHeight };
  } catch (ex) {
    console.error(ex);
    return {
      newHref: url,
      newWidth: width,
      newHeight: height,
    };
  }
}

export function optimizeImageUrl2(
  url: string,
  width: number | string = 0,
  height: number | string = 0,
  quality = "75",
): [string, number, number] {
  if (!width || width === "null") {
    width = 800;
  }
  if (!height || height === "null") {
    height = 600;
  }
  const params: { [key: string]: string | number } = {
    fm: "webp",
    q: quality,
    w: width,
    h: height,
  };

  if (url) {
    const paramsString = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join("&");

    const imageUrl = `${url}?${paramsString}`;
    if (url.startsWith("https:")) {
      return [imageUrl, width as number, height as number];
    }
    return [`https:${imageUrl}`, width as number, height as number];
  }

  return ["", 0, 0];
}

export function optimizeImageUrl(
  url: string,
  width = 0,
  height = 0,
  quality = "75",
) {
  const params: { [key: string]: string | number } = {
    fm: "webp",
    q: quality,
    w: width,
    h: height,
  };

  if (url) {
    const paramsString = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join("&");

    const imageUrl = `${url}?${paramsString}`;
    if (url.startsWith("https:")) {
      return imageUrl;
    }
    return `https:${imageUrl}`;
  }

  return "";
}

export function optimizeImageThumbUrl(
  url: string,
  width = 0,
  height = 0,
  quality = "75",
) {
  const squareSize = width < height ? width : height;
  const params: { [key: string]: string } = {
    fm: "webp",
    q: quality,
  };

  if (width && height) {
    Object.assign(params, {
      w: Math.floor(squareSize),
      h: Math.floor(squareSize),
      fit: "thumb",
    });
  }

  if (url) {
    const paramsString = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join("&");

    const imageUrl = `${url}?${paramsString}`;
    if (url.startsWith("https:")) {
      return imageUrl;
    }
    return `https:${imageUrl}`;
  }

  return "";
}

export function getImageSize(
  image: { width: number; height: number },
  max = 4000,
) {
  const maxWidth = max < 4000 ? max : 4000;
  const { width, height } = image;
  const horizontal = width >= height;

  const greater = horizontal ? width : height;
  const lesser = horizontal ? height : width;
  const aspect = horizontal ? width / height : height / width;

  if (greater <= maxWidth) {
    if (horizontal) {
      return { width: greater, height: lesser, aspect };
    } else {
      return { width: lesser, height: greater, aspect };
    }
  }
  const scale = maxWidth / greater;

  if (horizontal) {
    return {
      width: Math.floor(greater * scale),
      height: Math.floor(lesser * scale),
      aspect,
    };
  } else {
    return {
      width: Math.floor(lesser * scale),
      height: Math.floor(greater * scale),
      aspect,
    };
  }
}
